// ShansoLearnBanner — gradient brand banner for the landing-page hero.
//
// Replaces the previous "course list strip" with a single, undeniable hero
// moment for the ShansoLearn product. Massive gradient wordmark, tagline,
// 4 pillar pills (Flagship · Quick Bites · Lifetime · Certified), and a
// fat CTA. The entire banner is clickable; the inner CTA stops propagation
// so it can show focus state cleanly.
//
// Mounted in: /app/frontend/src/pages/LandingPage.jsx (hero, replaces
// FeaturedQuickBitesStrip).

import { useNavigate } from "react-router-dom";
import { GraduationCap, Zap, Infinity as InfinityIcon, Award, ArrowRight } from "lucide-react";

const PILLS = [
  { icon: GraduationCap, label: "Flagship Courses", sub: "Deep-dive · ₹1,999+" },
  { icon: Zap,           label: "Quick Bites",      sub: "20-min sprints · ₹399" },
  { icon: InfinityIcon,  label: "Lifetime Access",  sub: "No subscriptions" },
  { icon: Award,         label: "Verified Certs",   sub: "LinkedIn-shareable" },
];

export default function ShansoLearnBanner() {
  const navigate = useNavigate();
  const go = (e) => {
    e?.stopPropagation?.();
    navigate("/courses");
  };

  return (
    <div className="mt-12 sm:mt-16 w-full max-w-5xl mx-auto" data-testid="hero-shansolearn-banner">
      <button
        type="button"
        onClick={go}
        className="group relative w-full block rounded-3xl overflow-hidden border border-white/10 hover:border-white/25 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00F0FF]/60"
        style={{
          background: "radial-gradient(ellipse 80% 60% at 50% 0%, rgba(0,240,255,0.18), transparent 60%), radial-gradient(ellipse 80% 60% at 100% 100%, rgba(255,0,255,0.14), transparent 60%), radial-gradient(ellipse 60% 60% at 0% 100%, rgba(112,0,255,0.18), transparent 60%), #050510",
        }}
        data-testid="hero-shansolearn-banner-cta"
        aria-label="Go to ShansoLearn course catalog"
      >
        {/* Animated gradient sheen */}
        <span
          className="absolute inset-y-0 -left-1/2 w-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
          style={{
            background: "linear-gradient(115deg, transparent 0%, rgba(255,255,255,0.06) 50%, transparent 100%)",
            animation: "sl-banner-sheen 2.5s ease-in-out infinite",
          }}
        />

        {/* Soft grid pattern overlay (very subtle) */}
        <span
          className="absolute inset-0 opacity-[0.07] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
            backgroundSize: "32px 32px",
            maskImage: "radial-gradient(ellipse 70% 100% at 50% 0%, black, transparent)",
            WebkitMaskImage: "radial-gradient(ellipse 70% 100% at 50% 0%, black, transparent)",
          }}
        />

        <div className="relative px-6 sm:px-10 py-10 sm:py-14 flex flex-col items-center text-center">
          {/* LIVE tag */}
          <span
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-extrabold tracking-[0.18em] uppercase mb-5"
            style={{
              background: "rgba(163,230,53,0.12)",
              border: "1px solid rgba(163,230,53,0.4)",
              color: "#a3e635",
            }}
          >
            <span className="relative flex w-1.5 h-1.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-[#a3e635] opacity-75 animate-ping" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#a3e635]" />
            </span>
            ShansoLearn · Live
          </span>

          {/* Gradient wordmark */}
          <h2
            className="font-outfit font-black tracking-tight leading-[0.95] select-none"
            style={{
              fontSize: "clamp(36px, 6vw, 72px)",
              backgroundImage:
                "linear-gradient(110deg, #00F0FF 0%, #7000FF 40%, #FF00FF 70%, #00F0FF 100%)",
              backgroundSize: "200% auto",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              WebkitTextFillColor: "transparent",
              color: "transparent",
              animation: "sl-banner-shimmer 6s linear infinite",
              filter: "drop-shadow(0 8px 30px rgba(0,240,255,0.25))",
            }}
            data-testid="hero-shansolearn-wordmark"
          >
            ShansoLearn
          </h2>

          {/* Tagline */}
          <p className="mt-5 text-zinc-300 text-base sm:text-lg font-dm-sans max-w-2xl leading-relaxed">
            <span className="text-white font-semibold">Learn · Earn · Ship</span>
            <span className="text-zinc-500"> — </span>
            AI courses that respect your weekend.
          </p>

          {/* 4 pillar pills */}
          <div className="mt-7 sm:mt-9 grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-3 w-full max-w-3xl" data-testid="hero-shansolearn-pills">
            {PILLS.map((p) => (
              <div
                key={p.label}
                className="flex items-center gap-2.5 sm:flex-col sm:items-center sm:text-center px-3 sm:px-4 py-2.5 sm:py-3.5 rounded-xl border border-white/10 bg-white/[0.03] backdrop-blur-sm transition-colors group-hover:border-white/20"
                data-testid={`hero-shansolearn-pill-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div
                  className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg flex items-center justify-center shrink-0"
                  style={{
                    background: "linear-gradient(135deg, rgba(0,240,255,0.15), rgba(112,0,255,0.15))",
                    border: "1px solid rgba(0,240,255,0.25)",
                  }}
                >
                  <p.icon size={16} className="text-[#00F0FF]" strokeWidth={2} />
                </div>
                <div className="min-w-0 sm:mt-1">
                  <p className="text-white text-[12px] sm:text-[13px] font-bold leading-tight">{p.label}</p>
                  <p className="text-zinc-500 text-[10px] sm:text-[11px] leading-tight mt-0.5 truncate sm:whitespace-normal">{p.sub}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Fat CTA */}
          <span
            className="mt-8 sm:mt-10 inline-flex items-center gap-2 px-7 sm:px-9 py-3.5 sm:py-4 rounded-xl text-black font-extrabold text-base sm:text-lg font-outfit transition-transform group-hover:scale-[1.03]"
            style={{
              background: "linear-gradient(135deg, #00F0FF, #7000FF)",
              boxShadow: "0 18px 50px -15px rgba(0,240,255,0.55), 0 8px 24px -10px rgba(112,0,255,0.45)",
            }}
            data-testid="hero-shansolearn-enter-btn"
          >
            Enter ShansoLearn
            <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
          </span>

          {/* Footnote */}
          <p className="mt-4 text-zinc-500 text-[11px] sm:text-xs font-dm-sans">
            9 courses live · Self-paced video · One-time payment
          </p>
        </div>
      </button>

      <style>{`
        @keyframes sl-banner-shimmer {
          0%   { background-position: 0% 50%; }
          50%  { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes sl-banner-sheen {
          0%   { transform: translateX(0%); }
          100% { transform: translateX(300%); }
        }
      `}</style>
    </div>
  );
}
